import {
  useContext,
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import apiClient, { withOrganizationHeaders } from "../api/httpClient";
import authApi from "../api/authApi";
import endpoints from "../api/endpoints";
import { useAppTheme } from "../context/ThemeContext";
import { removeWebFcmToken } from "../services/firebaseMessaging";




const AuthContext = createContext();

const ORG_STORAGE_KEY = "iot-selected-org";
const EMAIL_STORAGE_KEY = "iot-email";
const RESET_PW_REQUIRED_STORAGE_KEY = "iot-reset-password-required";
const USER_STORAGE_KEY = "iot-user";

const PUBLIC_ROUTES = [
  "/login",
  "/signup",
  "/contact-us",
  "/verify-code",
  "/forgot-password",
  "/verify-reset-code",
  "/accept-invite",
  "/reset-password",
];

const hasAnyPermission = (permissionMap) =>
  Boolean(
    permissionMap &&
    typeof permissionMap === "object" &&
    Object.values(permissionMap).some(Boolean)
  );

const safeParseJSON = (val) => {
  try {
    return JSON.parse(val);
  } catch {
    return null;
  }
};

const toPositiveInt = (value) => {
  const normalized = Number(value);
  return Number.isInteger(normalized) && normalized > 0 ? normalized : null;
};

const isValidOrg = (o) => toPositiveInt(o?.id) !== null;

const normalizeOrg = (o, scopeSource = null) => {
  const id = toPositiveInt(o?.id);
  if (!id) return {};

  const normalized = {
    id,
    name: typeof o?.name === "string" ? o.name : (o?.name || ""),
  };

  const userId = toPositiveInt(o?.userId ?? o?.user_id ?? scopeSource?.id);
  const subid = toPositiveInt(o?.subid ?? scopeSource?.subid);

  if (userId) normalized.userId = userId;
  if (subid) normalized.subid = subid;

  return normalized;
};

const hasMatchingOrgScope = (storedOrg, scopeSource) => {
  const storedUserId = toPositiveInt(storedOrg?.userId ?? storedOrg?.user_id);
  const storedSubid = toPositiveInt(storedOrg?.subid);
  const scopeUserId = toPositiveInt(scopeSource?.id);
  const scopeSubid = toPositiveInt(scopeSource?.subid);

  return Boolean(
    storedUserId &&
    storedSubid &&
    scopeUserId &&
    scopeSubid &&
    storedUserId === scopeUserId &&
    storedSubid === scopeSubid
  );
};

const buildFallbackOrg = (userLike, organizationLike) => {
  if (isValidOrg(organizationLike)) {
    return normalizeOrg(organizationLike, userLike);
  }

  return normalizeOrg(
    {
      id: userLike?.organization_id,
      name: userLike?.organization_name || "",
    },
    userLike
  );
};

const isPasswordExpired = (dateStr) => {
  if (!dateStr) return false;
  const exp = new Date(dateStr);
  if (Number.isNaN(exp.getTime())) return false;
  exp.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return exp < today;
};

const requiresPasswordSetup = (userLike) =>
  Boolean(userLike?.password_initialization_required) ||
  isPasswordExpired(userLike?.password_expired_at);

const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
const { setMode } = useAppTheme();

  const [email, setEmail] = useState(localStorage.getItem(EMAIL_STORAGE_KEY) || "");
  const [permissions, setPermissions] = useState([]);
  const [user, setUser] = useState({});
  const [org, _setOrg] = useState({});

  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [passwordExpired, setPasswordExpired] = useState(false);
  const [resetPasswordRequired, _setResetPasswordRequired] = useState(
    () => sessionStorage.getItem(RESET_PW_REQUIRED_STORAGE_KEY) === "1"
  );
  const userRef = useRef({});

  const setResetPasswordRequired = useCallback((required) => {
    const next = Boolean(required);
    _setResetPasswordRequired(next);
    if (next) {
      sessionStorage.setItem(RESET_PW_REQUIRED_STORAGE_KEY, "1");
    } else {
      sessionStorage.removeItem(RESET_PW_REQUIRED_STORAGE_KEY);
    }
  }, []);

  const pathRef = useRef(location.pathname);
  useEffect(() => {
    pathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    userRef.current = user || {};
  }, [user]);

  useEffect(() => {
    try {
      if (user && Object.keys(user).length > 0) {
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
      } else {
        localStorage.removeItem(USER_STORAGE_KEY);
      }
    } catch (error) {
      console.error("Failed to persist user in localStorage:", error);
    }
  }, [user]);

  const logoutInProgressRef = useRef(false);

  const publicMeCheckedRef = useRef(false);

  const cleanUpAuthStates = useCallback(() => {
    setEmail("");
    setAuthed(false);
    setUser({});
    _setOrg({});
    setPermissions([]);
    setPasswordExpired(false);
    setResetPasswordRequired(false);
    localStorage.removeItem(EMAIL_STORAGE_KEY);
    localStorage.removeItem(ORG_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem("iot-token");
  }, [setResetPasswordRequired]);

  const applyOrgSelection = useCallback((nextOrg, scopeSource = userRef.current) => {
    const normalized = normalizeOrg(nextOrg, scopeSource);
    if (isValidOrg(normalized)) {
      _setOrg(normalized);
      localStorage.setItem(ORG_STORAGE_KEY, JSON.stringify(normalized));
    } else {
      _setOrg({});
      localStorage.removeItem(ORG_STORAGE_KEY);
    }
  }, []);

  const setOrg = useCallback((nextOrg) => {
    applyOrgSelection(nextOrg, userRef.current);
  }, [applyOrgSelection]);

  const orgInterceptorIdRef = useRef(null);
  useEffect(() => {
    if (orgInterceptorIdRef.current !== null) {
      apiClient.interceptors.request.eject(orgInterceptorIdRef.current);
    }

    orgInterceptorIdRef.current = apiClient.interceptors.request.use(
      (config) => withOrganizationHeaders(config, org),
      (error) => Promise.reject(error)
    );

    return () => {
      if (orgInterceptorIdRef.current !== null) {
        apiClient.interceptors.request.eject(orgInterceptorIdRef.current);
        orgInterceptorIdRef.current = null;
      }
    };
  }, [org]);

  const validatePersistedOrg = useCallback(async (candidateOrg) => {
    if (!isValidOrg(candidateOrg)) return false;

    try {
      const response = await apiClient.get(endpoints.organization.get(candidateOrg.id));
      return response?.data?.status === "OK";
    } catch (error) {
      const status = error?.response?.status;
      if (status === 400 || status === 403 || status === 404) {
        return false;
      }
      throw error;
    }
  }, []);

  const authResponseInterceptorIdRef = useRef(null);
  useEffect(() => {
    if (authResponseInterceptorIdRef.current !== null) {
      apiClient.interceptors.response.eject(authResponseInterceptorIdRef.current);
    }

    authResponseInterceptorIdRef.current = apiClient.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;

        if (status === 401 || status === 403) {
          cleanUpAuthStates();

          const currentPath = pathRef.current || "";
          const isPublicNow = PUBLIC_ROUTES.includes(currentPath);

          if (!logoutInProgressRef.current && !isPublicNow) {
            navigate("/login", { replace: true });
          }
        }

        return Promise.reject(error);
      }
    );

    return () => {
      if (authResponseInterceptorIdRef.current !== null) {
        apiClient.interceptors.response.eject(authResponseInterceptorIdRef.current);
        authResponseInterceptorIdRef.current = null;
      }
    };
  }, [cleanUpAuthStates, navigate]);

  const checkAuthStatus = useCallback(async () => {
    const path = location.pathname;

    if (logoutInProgressRef.current) {
      setLoading(false);
      return;
    }

    const isPublic = PUBLIC_ROUTES.includes(path);

    if (isPublic && authed) {
      setLoading(false);
      return;
    }

    if (isPublic && !authed) {
      if (publicMeCheckedRef.current) {
        setLoading(false);
        return;
      }
      publicMeCheckedRef.current = true;
    }

    setLoading(true);

    try {
      const response = await authApi.me();

      if (response.status === 200 && response.data.authed) {
        const meUser = response.data.user || {};
        const meOrg = response.data.organization || {};
        const mePerms = response.data.permissions || [];

        setUser(meUser);
        setPermissions(mePerms);
        setEmail(meUser.email || "");
        setAuthed(true);

        const expired = requiresPasswordSetup(meUser);
        setPasswordExpired(expired);

        const stored = safeParseJSON(localStorage.getItem(ORG_STORAGE_KEY));
        const fallbackOrg = buildFallbackOrg(meUser, meOrg);
        let nextOrg = isValidOrg(fallbackOrg) ? fallbackOrg : {};

        const scopedStoredOrg = hasMatchingOrgScope(stored, meUser)
          ? normalizeOrg(stored, meUser)
          : null;

        if (isValidOrg(scopedStoredOrg) && scopedStoredOrg.id !== nextOrg.id) {
          try {
            const storedOrgIsAccessible = await validatePersistedOrg(scopedStoredOrg);

            if (storedOrgIsAccessible) {
              nextOrg = scopedStoredOrg;
            } else {
              console.warn(
                `[Auth] Discarding stale selected organization ${scopedStoredOrg.id} for user ${meUser.id} (subid ${meUser.subid}).`
              );
            }
          } catch (validationError) {
            console.error(
              "[Auth] Failed to validate stored organization. Falling back to the user's current organization:",
              validationError
            );
          }
        }

        applyOrgSelection(nextOrg, meUser);

        if (meUser.email) localStorage.setItem(EMAIL_STORAGE_KEY, meUser.email);

        if (isPublic && path !== "/contact-us") {
          navigate(
            expired
              ? "/force-change-password"
              : hasAnyPermission(mePerms)
                ? "/devices"
                : "/contact-us",
            { replace: true }
          );
        } else if (!isPublic && !hasAnyPermission(mePerms)) {
          navigate("/contact-us", { replace: true });
        }
      } else {
        cleanUpAuthStates();

        if (!isPublic) {
          navigate("/login", { replace: true });
        }
      }
    } catch (error) {
      const status = error?.response?.status;

      if (status === 401 || status === 403) {
        cleanUpAuthStates();
        if (!isPublic) navigate("/login", { replace: true });
      } else {
        console.error("Auth check failed (network/server). Keeping session:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [location.pathname, authed, navigate, cleanUpAuthStates, applyOrgSelection, validatePersistedOrg]);

  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

const loginAction = async (emailArg, password, alert) => {
  try {
    publicMeCheckedRef.current = false;
    logoutInProgressRef.current = false;

    const response = await authApi.login(emailArg, password);

    const resp = response.data;

    if (resp.message === "success") {
      const u = resp.user || {};
      setUser(u);

      const userTheme = u.settings?.others?.theme;
      if (userTheme === "light") setMode("light");
      else if (userTheme === "dark") setMode("dark");

      const nextPermissions = resp.permissions || [];
      setPermissions(nextPermissions);
      setEmail(emailArg);
      setAuthed(true);
      setResetPasswordRequired(false);
      localStorage.setItem(EMAIL_STORAGE_KEY, emailArg);

      applyOrgSelection(buildFallbackOrg(u, resp.organization), u);

      const expired = requiresPasswordSetup(u);
      setPasswordExpired(expired);

      navigate(
        expired ? "/force-change-password" : hasAnyPermission(nextPermissions) ? "/devices" : "/contact-us",
        { replace: true }
      );
      return;
    }

    await alert({
      variant: "error",
      icon: "error",
      title: "Login Failed",
      message: "The email or password you entered is incorrect.\nPlease try again.",
      confirmText: "Ok",
    });

    return;
  } catch (err) {
    console.error("Login error:", err);

    const status = err?.response?.status;
    const isInvalidCredentials = status === 401 || status === 422;

    await alert({
      variant: "error",
      icon: "error",
      title: "Login Failed",
      message: "The email or password you entered is incorrect.\nPlease try again.",
      confirmText: "Ok",
    });

    if (isInvalidCredentials) return;

    cleanUpAuthStates();
  }
};


  const signupAction = async (emailArg, name, application = "") => {
    const response = await authApi.signup({ email: emailArg, name, application });
    return response.data;
  };

  const verifyAction = async (emailArg, code, password, application = "") => {
    try {
      publicMeCheckedRef.current = false;
      logoutInProgressRef.current = false;

      const response = await authApi.verifyCode({
        email: emailArg,
        code,
        password,
        application,
      });

      const resp = response.data;

      if (resp.success) {
        const u = resp.user || {};
        setUser(u);
        const userTheme = u.settings?.others?.theme;
        if (userTheme === "light") setMode("light");
        else if (userTheme === "dark") setMode("dark");
        const nextPermissions = resp.permissions || [];
        setPermissions(nextPermissions);
        setEmail(emailArg);
        setAuthed(true);
        setResetPasswordRequired(false);
        localStorage.setItem(EMAIL_STORAGE_KEY, emailArg);

        applyOrgSelection(buildFallbackOrg(u, resp.organization), u);

        const expired = requiresPasswordSetup(u);
        setPasswordExpired(expired);

        navigate(
          expired ? "/force-change-password" : hasAnyPermission(nextPermissions) ? "/devices" : "/contact-us",
          { replace: true }
        );
      } else {
        window.alert("Verification failed: " + (resp.message || "Unknown error"));
      }
    } catch (err) {
      console.error("Verify error:", err);
      const msg = err.response?.data?.message || "Verification failed";
      window.alert(msg);
      cleanUpAuthStates();
      throw new Error(msg);
    }
  };

  const forgotPasswordAction = async (emailArg) => {
    try {
      const response = await authApi.forgotPassword(emailArg);
      return response.data;
    } catch (err) {
      console.error("Forgot Password error:", err);
      const msg =
        err.response?.data?.message || "Error sending code: Network or Server issue.";
      throw new Error(msg);
    }
  };

  const verifyResetCodeAction = async (emailArg, code) => {
    try {
      const response = await authApi.verifyResetCode(emailArg, code);

      const resp = response.data;

      if (resp.success) {
        const u = resp.user || {};
        setUser(u);
        const userTheme = u.settings?.others?.theme;
        if (userTheme === "light") setMode("light");
        else if (userTheme === "dark") setMode("dark");
        setPermissions(resp.permissions || []);
        setEmail(emailArg);
        setAuthed(true);
        setResetPasswordRequired(true);
        localStorage.setItem(EMAIL_STORAGE_KEY, emailArg);

        applyOrgSelection(buildFallbackOrg(u, resp.organization), u);

        return resp;
      } else {
        throw new Error(resp.message || "Verification failed");
      }
    } catch (err) {
      console.error("Verify Reset Code error:", err);
      const msg = err.response?.data?.message || "Verification failed";
      throw new Error(msg);
    }
  };

  const resetPasswordAction = async (newPassword) => {
    try {
      const response = await authApi.resetPassword(newPassword);

      if (response.data.success) setPasswordExpired(false);
      if (response.data.success) setResetPasswordRequired(false);
      return response.data;
    } catch (err) {
      console.error("Reset Password error:", err);
      const msg = err.response?.data?.message || "Password reset failed";
      throw new Error(msg);
    }
  };

  const logOut = async () => {
    logoutInProgressRef.current = true; 

    let webFcmToken = "";
    try {
      webFcmToken = await removeWebFcmToken();
    } catch (err) {
      console.error("Failed to remove browser notification token:", err);
    }

    try {
      await authApi.logout(webFcmToken);
    } catch (err) {
      console.error("Failed to clear cookie on logout:", err);
    }

    cleanUpAuthStates();

    publicMeCheckedRef.current = true;

    navigate("/login", { replace: true });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        org,
        setOrg,
        permissions,
        setPermissions,
        email,
        authed,
        loading,
        passwordExpired,
        setPasswordExpired,
        resetPasswordRequired,
        loginAction,
        signupAction,
        verifyAction,
        forgotPasswordAction,
        verifyResetCodeAction,
        resetPasswordAction,
        logOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;

export const useAuth = () => useContext(AuthContext);
